// Loads the server-rendered report PDF with pdf.js and prints it: each page is rasterized and laid out as an A4
// sheet, so "Genera report PDF" opens the browser's print dialog (and PDF saving) as the page's old HTML report did.
import type { PDFDocumentProxy } from "pdfjs-dist";

const ROOT_CLASS = "scReportPdfPrintRoot";
const STYLE_ID = "scReportPdfPrintStyle";
// Page pixels per PDF point: about 216 dpi, crisp in print and still light enough for a long report.
const PRINT_SCALE = 3;

// pdf.js is loaded with the first report: the page bundle doesn't carry it.
async function pdfjs() {
  const [pdfjs, worker] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

export async function loadReportPdf(url: string): Promise<PDFDocumentProxy> {
  const lib = await pdfjs();
  return lib.getDocument({ url }).promise;
}

const PRINT_STYLES = `
@page { size: A4; margin: 0; }
@media screen { .${ROOT_CLASS} { display: none !important; } }
@media print {
  html, body {
    height: auto !important; min-height: 0 !important; overflow: visible !important;
    background: #fff !important; margin: 0 !important; padding: 0 !important;
  }
  body > :not(.${ROOT_CLASS}) { display: none !important; }
  .${ROOT_CLASS} { display: block !important; margin: 0; padding: 0; }
  .${ROOT_CLASS} .sheet {
    width: 210mm; height: 297mm;
    break-after: page; page-break-after: always;
  }
  .${ROOT_CLASS} .sheet:last-child { break-after: auto; page-break-after: auto; }
  .${ROOT_CLASS} img { display: block; width: 100%; height: 100%; object-fit: contain; }
}`;

function cleanup() {
  document.getElementById(STYLE_ID)?.remove();
  document.querySelectorAll(`.${ROOT_CLASS}`).forEach((el) => el.remove());
}

export async function printReportPdf(url: string): Promise<void> {
  cleanup();
  const doc = await loadReportPdf(url);
  const root = document.createElement("div");
  root.className = ROOT_CLASS;
  const objectUrls: string[] = [];
  try {
    for (let p = 1; p <= doc.numPages; p++) {
      const page = await doc.getPage(p);
      const viewport = page.getViewport({ scale: PRINT_SCALE });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, viewport }).promise;
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      canvas.width = 0;
      canvas.height = 0;
      if (!blob) throw new Error("report page rendering failed");
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      const sheet = document.createElement("div");
      sheet.className = "sheet";
      const img = document.createElement("img");
      img.src = objectUrl;
      sheet.appendChild(img);
      root.appendChild(sheet);
    }

    document.body.appendChild(root);
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = PRINT_STYLES;
    document.head.appendChild(style);

    await Promise.all([...root.querySelectorAll("img")].map((img) => img.decode().catch(() => undefined)));
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        window.removeEventListener("afterprint", finish);
        cleanup();
        resolve();
      };
      window.addEventListener("afterprint", finish);
      window.print();
      // Browsers where print() returns before the dialog closes still fire afterprint; this is only a safety net.
      setTimeout(finish, 60_000);
    });
  } finally {
    cleanup();
    objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    await doc.loadingTask.destroy();
  }
}
